import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useBillingOverviewQuery, useSubscriptionQuery, type BillingOverview, type PlanTier } from '@entities/Plan';
import { paths } from '@shared/config';
import { Button, Card, Chip } from '@shared/ui';

const PLAN_LABEL: Record<PlanTier, string> = {
  DEMO: 'Demo',
  PRO: 'Pro',
  PREMIUM: 'Max',
};

function formatDate(iso: string | null, lang: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

interface SubscriptionCardProps {
  /** Показывать ли карточку, когда тариф бесплатный (DEMO). На «Тарифах» сетка и
   *  так показывает текущий план, поэтому там передаём false. */
  showFreeState?: boolean;
}

export function SubscriptionCard({ showFreeState = true }: SubscriptionCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const subQuery = useSubscriptionQuery();
  const lang = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];
  const isFree = subQuery.data?.plan === 'DEMO';
  // Жетоны Demo в день и несгораемые — из того же обзора, что «Тарифы» (ТЗ демо 1.9): одно состояние одним
  // именем. Demo бесплатно и без срока с 2026-10-22.
  const overviewQuery = useBillingOverviewQuery({ enabled: isFree && showFreeState });

  const sub = subQuery.data;
  if (!sub) return null;
  if (isFree && !showFreeState) return null;

  return (
    <Card padding="lg" className="mb-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Card.Kicker className="mb-1.5">{t('subscription.title')}</Card.Kicker>

          {isFree ? (
            <DemoState overview={overviewQuery.data ?? null} loading={overviewQuery.isPending} />
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                <span className="font-serif text-[22px] font-medium tracking-[-0.01em]">
                  {PLAN_LABEL[sub.plan]}
                </span>
                <Chip variant="due">{t('subscription.activeChip')}</Chip>
              </div>
              <p className="mt-1.5 text-[14px] leading-relaxed text-muted">
                {t('subscription.activeUntil', { date: formatDate(sub.planExpiresAt, lang) })}
              </p>
            </>
          )}
        </div>

        <div className="shrink-0">
          {isFree ? (
            <Button variant="ghost" onClick={() => navigate(paths.plans)}>
              {t('subscription.choosePlan')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => navigate(paths.plans)}>
              {t('plans.extend')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Тариф Demo: бесплатно и без срока — сколько жетонов в день и сколько несгораемых. */
function DemoState({ overview, loading }: { overview: BillingOverview | null; loading: boolean }) {
  const { t } = useTranslation();
  const line = overview
    ? [
        t('subscription.demo.free', { count: overview.prices.demoDaily }),
        overview.quota.bonus > 0 ? t('subscription.demo.bonus', { count: overview.quota.bonus }) : '',
      ]
        .filter(Boolean)
        .join(' ')
    : null;

  return (
    <>
      <div className="font-serif text-[22px] font-medium tracking-[-0.01em]">{t('subscription.demo.title')}</div>
      {line ? (
        <p className="mt-1.5 max-w-[520px] text-[14px] leading-relaxed text-muted">{line}</p>
      ) : loading ? (
        // Обзор тарифа ещё грузится — скелетон строки той же высоты (60% от 520 px строки).
        <div aria-hidden className="mt-1.5 flex h-[23px] items-center">
          <div className="h-[14px] w-[312px] max-w-full animate-skeleton rounded bg-elev" />
        </div>
      ) : null}
    </>
  );
}
